'use client';

import React, { useState, useRef, useEffect } from 'react';
import { CiShoppingTag } from "react-icons/ci";
import { useTranslations } from "next-intl"
import { IoMdCloseCircle } from "react-icons/io";
import {useLocale} from 'next-intl';
import MailChecker from "mailchecker";
import validator from "validator";
import moment from 'moment';
import { FaCircleNotch } from "react-icons/fa"; 
import { GrClose } from "react-icons/gr";


const ShopletterDialog: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false); // Loading state
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null); // Ref for the dialog container
  const t = useTranslations('ShopLetter')
  const l = useLocale()
  const te = useTranslations('Login');
  const tn = useTranslations('TopNav');
  const now = moment();

 
   const isValidEmail = async (email: string): Promise<{ valid: boolean; message?: string }> => {
     // Step 1: Check if the email is empty or null
     if (!email || email.trim() === "") {
       return { valid: false, message: te('Email-is-required') };
     }
 
     // Step 2: Validate email format using regex
     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
     if (!emailRegex.test(email)) {
       return { valid: false, message: te('Invalid-email-format') };
     }
 
     // Step 3: Validate email format using validator library
     if (!validator.isEmail(email)) {
       return { valid: false, message: te('Invalid-email-format') };
     }
 
     // Step 4: Check if the email is disposable using mailchecker
     if (!MailChecker.isValid(email)) {
       return { valid: false, message: te('Disposable-emails') };
     }
 
     // If all checks pass, the email is valid
     return { valid: true };
   };
 
   const handleSubscribe = async () => {
     const emailValidation = await isValidEmail(email);
     if (!emailValidation.valid) {
       setError(emailValidation.message || te('Invalid-email'));
       return; // Exit the function if the email is invalid
     }
 
     setIsLoading(true);
     const language = l;
     const date = now.format('MMMM Do YYYY');
     const time = now.format('h:mm:ss a');
 
     try {
       const response = await fetch(`${process.env.NEXT_PUBLIC_URL}emailletterpost/`, {
         method: "POST",
         headers: {
           "Content-Type": "application/json",
           "Authorization": "Token "+process.env.NEXT_PUBLIC_TOKEN,
         },
         body: JSON.stringify({
           email,
           language,
           date,
           time,
         }),
       });
 
       // Log the raw response for debugging
       const rawResponse = await response.text();
 
       if (!response.ok) {
         // Handle HTTP errors
         const errorData = JSON.parse(rawResponse); // Try parsing the raw response
         setError(errorData.message || te('Subscription-failed'));
         return;
       }
 
       const data = JSON.parse(rawResponse); // Parse the raw response as JSON
       setIsSubscribed(true);
       setIsLoading(false);
       setError('');
       setEmail('');
     } catch (error) {
       console.error(te('Error-uring-subscription'), error);
       setError(te('Network-error'));
     } finally {
       setIsLoading(false);
     }
   };
 

  // Close the dialog when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        setIsOpen(false); // Close the dialog
      }
    };

    // Add event listener when the dialog is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]); // Re-run effect when `isOpen` changes

  return (
    <>
      {/* Button to open the dialog */}

      <button
        onClick={() => setIsOpen(true)}
        className="mt-2 px-5 sm:px-20 py-4 bg-secondary hover:bg-yel hover:border hover:border-secondary text-yel hover:text-secondary rounded-md  gap-3 uppercase flex items-center"
      >
     {t('button')}
<CiShoppingTag size={22}/>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div
            ref={dialogRef} // Attach the ref to the dialog container
            className="bg-yel rounded-lg shadow-lg p-6 max-w-md w-full relative "
          >
            {/* Close Button */}
            {l === "ar"? (<GrClose size={24}    className="absolute top-2  left-2 text-bl hover:text-gray-700" onClick={() => setIsOpen(false)}/>):
            (<GrClose size={24}    className="absolute top-2  right-2 text-bl hover:text-gray-700" onClick={() => setIsOpen(false)}/>)}
            
            {/* Content */}
            {isSubscribed ? (
            <div >
                <h2 className="text-xl font-semibold text-green-600 mb-4 uppercase text-center">{t('success-title')}</h2>
                <p className="text-gray-600 text-sm mb-4">{tn('success-description')}</p>
                <p className="text-gray-600 text-sm">{tn('success-description-1')}</p>
              </div>
              ) : (
                <>
                  <h2 className="text-xl font-semibold text-gray-800  mb-4 uppercase ">{t('newsletter-title')}</h2>
                  <p className="text-gray-600 mb-6 text-sm">
                  {t('newsletter-description')}
                  </p>
                  <div className="flex flex-col space-y-4">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('newsletter-email-input')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-yel"
                    />
                    {error && <p className="text-bl text-sm">{error}</p>}
                
                 {isLoading ? (
                                    <button
                                    aria-label= {t('newsletter-subscribe-button')}
                                      onClick={handleSubscribe}
                                      className="w-full hover:bg-secondary hover:text-yel py-2 rounded-lg font-medium bg-yel ext-black transition-colors uppercase flex gap-3 justify-center items-center"
                                    >
                                     {t('newsletter-subscribe-button')}
                                      <FaCircleNotch className="animate-spin w-5 h-5"/>
                                    </button>
                                  ) : (
                                    <button
                                    aria-label= {t('newsletter-subscribe-button')}
                                      onClick={handleSubscribe}
                                      className="w-full bg-secondary text-yel py-2 rounded-lg hover:bg-yel font-medium hover:text-black transition-colors uppercase"
                                    >
                                   {t('newsletter-subscribe-button')}
                                    </button>
                                  )}
                 

                    <p className='text-gray-500 text-xs '>
                    {t('security-msg')}
  </p>
                  </div>
                </>
              )}
          </div>
        </div>
      )}
    </>
  );
};

export default ShopletterDialog;

